import { getSupabaseClient } from './supabase';

export type SupabaseEdgeRequestOptions = {
  /** The edge function path (e.g. 'api/address/lookup'). */
  path: string;
  /** HTTP method. Defaults to 'POST'. */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Request body (will be JSON-serialized by supabase-js). */
  body?: Record<string, unknown> | FormData;
  /** Request timeout in milliseconds. Defaults to 15000 (15s). Set to 0 to disable. */
  timeoutMs?: number;
};

/**
 * Thin wrapper around `supabase.functions.invoke` that provides:
 * - A single generic `request<T>()` method with typed responses
 * - Consistent error handling (throws on failure)
 * - Centralised access point so individual services don't depend on supabase-js directly
 */
export class SupabaseEdge {
  private static instance: SupabaseEdge | null = null;

  static getInstance(): SupabaseEdge {
    if (!SupabaseEdge.instance) {
      SupabaseEdge.instance = new SupabaseEdge();
    }
    return SupabaseEdge.instance;
  }

  private static readonly DEFAULT_TIMEOUT_MS = 15_000;

  /**
   * Invoke a Supabase Edge Function and return typed data.
   *
   * @throws Error when the invocation fails, returns an error, or times out.
   */
  async request<T>(options: SupabaseEdgeRequestOptions): Promise<T | null> {
    const { path, method, body, timeoutMs = SupabaseEdge.DEFAULT_TIMEOUT_MS } = options;
    const supabase = getSupabaseClient();

    const invoke = supabase.functions.invoke<T>(path, {
      ...(method && { method }),
      ...(body && { body }),
    });

    const { data, error } = timeoutMs > 0 ? await this.withTimeout(invoke, timeoutMs) : await invoke;

    if (error) {
      this.handleAuthError(error);
      throw error;
    }

    if (this.isAuthError(data)) {
      await this.clearAuth();
      throw new Error('Invalid JWT');
    }

    return data ?? null;
  }

  /**
   * Race a promise against a timeout. Rejects with an error if the timeout fires first.
   */
  private withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(`Request timed out after ${ms}ms`)), ms);
      promise
        .then((value) => {
          clearTimeout(timer);
          resolve(value);
        })
        .catch((err) => {
          clearTimeout(timer);
          reject(err);
        });
    });
  }

  /**
   * Check if a thrown error signals an invalid/expired JWT and clear auth if so.
   */
  private handleAuthError(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);
    if (message.includes('Invalid JWT') || message.includes('invalid JWT')) {
      this.clearAuth();
    }
  }

  /**
   * Check if the response data is an auth error object returned by the edge function.
   */
  private isAuthError(data: unknown): boolean {
    return (
      typeof data === 'object' &&
      data !== null &&
      'error' in data &&
      typeof (data as Record<string, unknown>).error === 'string' &&
      ((data as Record<string, string>).error.includes('Invalid JWT') ||
        (data as Record<string, string>).error.includes('invalid JWT'))
    );
  }

  /**
   * Remove the persisted auth session from storage and sign out.
   */
  private async clearAuth(): Promise<void> {
    try {
      const supabase = getSupabaseClient();
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      // Fallback: remove the auth storage key directly
      localStorage.removeItem('auth');
    }
  }
}
